import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { financialInstitutions } from './financialInstitutionsData';
import FinancialInstitutionAutocomplete from './FinancialInstitutionAutocomplete';
import InstitutionAutocomplete from './InstitutionAutocomplete';
import { useUser } from '../contexts/UserContext';

const STATUS_OPTIONS = ['In Progress', 'Submitted', 'In Review', 'Accepted', 'Rejected'];

const Dashboard = () => {
  const { user } = useUser();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    if (user === undefined) return; // auth loading
    if (!user) return;
    const q = query(collection(db, 'applications'), where('userId', '==', user.uid));
    const unsub = onSnapshot(q, (snapshot) => {
      setApplications(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (err) => {
      setError('Error loading applications.');
      setLoading(false);
    });
    return () => unsub();
  }, [user]);

  const handleNewApplication = async () => {
    setCreating(true);
    setError('');
    try {
      await addDoc(collection(db, 'applications'), {
        userId: user.uid,
        institution: '',
        type: '',
        status: 'In Progress',
        deadline: '',
        created: serverTimestamp(),
        updated: serverTimestamp(),
      });
    } catch (err) {
      setError('Could not create application.');
    }
    setCreating(false);
  };

  const openModal = (app) => {
    setSelectedApp(app);
    setModalOpen(true);
    setModalError('');
  };
  const closeModal = () => {
    setModalOpen(false);
    setSelectedApp(null);
    setModalError('');
  };

  const handleModalChange = (field, value) => {
    setSelectedApp(prev => ({ ...prev, [field]: value, updated: new Date() }));
  };

  const handleModalSave = async () => {
    setModalSaving(true);
    setModalError('');
    try {
      await updateDoc(doc(db, 'applications', selectedApp.id), {
        institution: selectedApp.institution,
        type: selectedApp.type,
        status: selectedApp.status,
        applyUrl: selectedApp.applyUrl || '',
        deadline: selectedApp.deadline || '',
        updated: serverTimestamp(),
      });
      closeModal();
    } catch (err) {
      setModalError('Could not save changes.');
    }
    setModalSaving(false);
  };

  const handleModalDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    setModalSaving(true);
    setModalError('');
    try {
      await deleteDoc(doc(db, 'applications', selectedApp.id));
      closeModal();
    } catch (err) {
      setModalError('Could not delete application.');
    }
    setModalSaving(false);
  };

  if (user === undefined) {
    return <div>Loading...</div>;
  }
  if (!user) {
    return <div>You must be logged in to view this page.</div>;
  }
  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: 24, background: 'transparent' }}>
      <h1>Applications Dashboard</h1>
      <div style={{fontWeight:700, fontSize:'1.18rem', color:'#43cea2', marginBottom:8, marginTop:6, letterSpacing:0.2}}>Empowering Future Tech Leaders 🚀</div>
      <p className="text-muted">Track and manage your tertiary education and financial aid applications here.</p>
      <div style={{ color: '#fff', fontSize: 16, margin: '24px 0', textShadow: '0 2px 8px #0007' }}>
        <b>Note:</b> Only the top 10 financial aid/bursary institutions are listed below by default. Use search to find more.
      </div>
      <button onClick={handleNewApplication} disabled={creating} style={{ marginBottom: 28 }}>
        {creating ? 'Creating...' : 'New Application'}
      </button>
      {loading && <div className="text-muted" style={{ marginTop: 32 }}>Loading applications...</div>}
      {error && <div className="text-error" style={{ marginTop: 16 }}>{error}</div>}
      {applications.length === 0 && !loading && <div className="text-muted" style={{ marginTop: 32 }}>No applications yet. Click "New Application" to start.</div>}
      {/* Reminder banner for upcoming deadlines */}
      {applications.length > 0 && (() => {
        const today = new Date();
        const in7Days = new Date();
        in7Days.setDate(today.getDate() + 7);
        const soon = applications.filter(app => {
          if (!app.deadline) return false;
          const d = new Date(app.deadline);
          return d >= today && d <= in7Days;
        });
        return soon.length > 0 ? (
          <div className="banner-warning">
            Reminder: You have {soon.length} application{soon.length > 1 ? 's' : ''} with deadlines in the next 7 days!
          </div>
        ) : null;
      })()}
      {applications.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Institution/Fund</th>
                <th>Type</th>
                <th>Status</th>
                <th>Deadline</th>
                <th>Last Updated</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {applications.map(app => (
                <tr key={app.id}>
                  <td>{app.institution || <span className="text-muted">(Not set)</span>}</td>
                  <td>{app.type || <span className="text-muted">(Not set)</span>}</td>
                  <td style={{ fontWeight: 600, color: app.status === 'Submitted' ? 'var(--primary)' : 'var(--accent)' }}>{app.status}</td>
                  <td style={{ fontWeight: 600,
                    color: (() => {
                      if (!app.deadline) return 'var(--muted)';
                      const d = new Date(app.deadline);
                      const today = new Date();
                      const in7Days = new Date();
                      in7Days.setDate(today.getDate() + 7);
                      if (d < today) return 'var(--error)'; // past deadline
                      if (d >= today && d <= in7Days) return 'var(--warning)'; // soon
                      return 'var(--primary)'; // future
                    })(),
                  }}>{app.deadline ? new Date(app.deadline).toLocaleDateString() : <span className="text-muted">(No deadline)</span>}</td>
                  <td>{app.updated?.toDate ? app.updated.toDate().toLocaleString() : ''}</td>
                  <td>
                    <button className="secondary" onClick={() => openModal(app)}>
                      View/Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Modal for View/Edit Application */}
      {modalOpen && selectedApp && (
        <div className="modal">
          <div className="modal-content">
            <h2>Edit Application</h2>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600 }}>Institution/Fund</label>
              {/* Searchable dropdown for financial institutions if type is Financial Aid or Bursary */}
              {(selectedApp.type === 'Financial Aid' || selectedApp.type === 'Bursary') ? (
                <FinancialInstitutionAutocomplete
                  value={selectedApp.institution || ''}
                  onSelect={(inst) => {
                    handleModalChange('institution', inst.name);
                    handleModalChange('applyUrl', inst.applyUrl);
                  }}
                  onInputChange={val => handleModalChange('institution', val)}
                />
              ) : (
                <InstitutionAutocomplete
                  value={selectedApp.institution || ''}
                  onSelect={inst => {
                    handleModalChange('institution', inst.name);
                    handleModalChange('applyUrl', inst.applyUrl);
                  }}
                  onInputChange={val => handleModalChange('institution', val)}
                />
              )}
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600 }}>Application Link (optional)</label>
              <input type="text" value={selectedApp.applyUrl || ''} onChange={e => handleModalChange('applyUrl', e.target.value)} placeholder="Paste the application URL here..." style={{ width: '100%', padding: 10, marginTop: 4, borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600 }}>Deadline (optional)</label>
              <input
                type="date"
                value={selectedApp.deadline || ''}
                onChange={e => handleModalChange('deadline', e.target.value)}
                style={{ width: '100%', padding: 10, marginTop: 4, borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
              />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600 }}>Type</label>
              <input type="text" value={selectedApp.type || ''} onChange={e => handleModalChange('type', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 4, borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600 }}>Status</label>
              <select value={selectedApp.status || 'In Progress'} onChange={e => handleModalChange('status', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 4, borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}>
                {STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            {modalError && <div style={{ color: '#b71c1c', marginBottom: 8 }}>{modalError}</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
              <button onClick={closeModal} style={{ padding: '10px 22px', borderRadius: 8, background: '#888', color: '#fff', border: 'none', fontWeight: 700, fontSize: 16 }} disabled={modalSaving}>Cancel</button>
              <button onClick={handleModalSave} style={{ padding: '10px 22px', borderRadius: 8, background: '#2d4b2d', color: '#fff', border: 'none', fontWeight: 700, fontSize: 16 }} disabled={modalSaving}>{modalSaving ? 'Saving...' : 'Save'}</button>
              <button onClick={handleModalDelete} style={{ padding: '10px 22px', borderRadius: 8, background: '#b71c1c', color: '#fff', border: 'none', fontWeight: 700, fontSize: 16 }} disabled={modalSaving}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default Dashboard;
